import clsx from "clsx";
import React, { useState } from "react";
import { DialogProps, TextField, Button, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import {
  ExerciseTagDto,
  TagDto,
} from "@growth-machine-llc/stridist-api-client";
import { BaseDialog } from "../../dialog/BaseDialog";
import { useCreateExerciseTag } from "./hooks/useCreateExerciseTag";
import { useCurrentWorkspace } from "../../../hooks/useCurrentWorkspace";
import { FieldsGroup } from "../../fields/FieldsGroup";
import { FieldError } from "../../fields/FieldError";
import { useGenericErrorHandler } from "../../../hooks/useGenericErrorHandler";
import { useUpdateExerciseTag } from "./hooks/useUpdateExerciseTag";
import {
  MAX_HEX_COLOR_VALUE,
  TagColorPicker,
} from "../../fields/TagColorPicker";
import LoadingButton from "../../button/LoadingButton";
import { useCreateClientTag } from "../../coach-clients-tags/mutations/useCreateClientTag";
import { useUpdateClientTag } from "../../coach-clients-tags/mutations/useUpdateClientTag";
import { TagType } from "../../../constants";
import { useUpsertTag } from "./hooks/useUpsertTag";

export const generateRandomColor = () =>
  "#" +
  Math.floor(Math.random() * MAX_HEX_COLOR_VALUE)
    .toString(16)
    .padStart(6, "0");

const COMMON_SPACING = 7;

const useStyles = makeStyles((theme) => ({
  root: {},

  paper: {
    width: 524,
  },

  wrapper: {
    display: "flex",
    alignItems: "center",
  },

  avatar: {
    width: theme.spacing(COMMON_SPACING),
    height: theme.spacing(COMMON_SPACING),
    marginRight: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },

  input: {
    height: theme.spacing(COMMON_SPACING),
    marginBottom: theme.spacing(1),
  },

  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(3),
  },

  label: {
    fontWeight: 700,
    fontSize: 16,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },

  optionClassName: {
    marginRight: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
    },
  },

  control: {
    marginBottom: theme.spacing(4),
    width: "100%",
  },

  error: {
    position: "absolute",
  },

  fieldGroupLabel: {
    marginBottom: theme.spacing(1),
  },
}));

export interface ManageTagDialogProps extends DialogProps {
  tag?: ExerciseTagDto | TagDto;
  onCreated?: (tag: ExerciseTagDto | TagDto) => void;
  tagType: TagType;
}

const MAX_TITLE_LENGTH = 20;

export function ManageTagDialog(props: ManageTagDialogProps) {
  const { className, onClose, tag, tagType, onCreated, ...other } = props;
  const s = useStyles();
  const workspace = useCurrentWorkspace();

  const { mutate: upsertTag, isPending } = useUpsertTag({
    tagType: tagType,
    id: tag?.id,
  });

  const [title, setTitle] = React.useState(tag?.title || "");
  const [description, setDescription] = React.useState(tag?.description || "");
  const [color, setColor] = useState<string | null>(
    tag?.primaryColor || generateRandomColor(),
  );

  const [error, setError] = useState<string>("");

  const inFlight = isPending;

  const [errors, setErrors] = React.useState({ title: "", description: "" });
  const onError = useGenericErrorHandler({
    disableDefaultSnackbar: false,
    setFieldError: (field, message) => {
      setErrors((errors) => ({ ...errors, [field]: message }));
    },
  });

  const handleTitleChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setTitle(event.target.value);
      const trimmedValue = event.target.value.trim();
      if (
        errors.title &&
        trimmedValue &&
        trimmedValue.length <= MAX_TITLE_LENGTH
      ) {
        setErrors((errors) => ({ ...errors, title: "" }));
      }
    },
    [errors],
  );

  const handleDescriptionChange = React.useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      setDescription(event.target.value);
      if (errors.description && event.target.value.trim()) {
        setErrors((errors) => ({ ...errors, description: "" }));
      }
    },
    [errors],
  );

  const resetState = React.useCallback(() => {
    setTitle("");
    setDescription("");
    setColor(null);
  }, []);

  const handleClose = React.useCallback(() => {
    resetState();
    onClose({}, "backdropClick");
  }, [onClose, resetState]);

  const handleUpsert = React.useCallback(() => {
    if (title.trim().length > MAX_TITLE_LENGTH) {
      setErrors((errors) => ({
        ...errors,
        title: `Title cannot exceed ${MAX_TITLE_LENGTH} characters.`,
      }));
      return;
    }
    upsertTag(
      {
        ...tag,
        title,
        description,
        primaryColor: color,
      },
      {
        onError: (error) => {
          onError(error);
        },
        onSuccess: () => {
          handleClose();
        },
      },
    );
  }, [tag?.id, handleClose, onError, upsertTag, title, description, color]);

  const handleColorChange = React.useCallback(
    (primaryColor: string) => {
      setColor(primaryColor);
    },
    [setColor, color],
  );

  const disabled = inFlight;

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      title={`${tag?.id ? "Edit" : "Create"} tag`}
      closeDisabled={disabled}
      onClose={handleClose}
      PaperProps={{ className: s.paper }}
      {...other}
    >
      <FieldsGroup
        className={clsx(s.control)}
        labelClassName={s.fieldGroupLabel}
        label="Name"
        {...other}
      >
        <TextField
          variant="outlined"
          fullWidth
          id="name"
          disabled={disabled}
          placeholder="Enter tag name..."
          value={title}
          onChange={handleTitleChange}
          required
        />
        {errors.title && (
          <FieldError hideIcon={true} className={s.error}>
            {errors.title}
          </FieldError>
        )}
      </FieldsGroup>
      <FieldsGroup
        className={clsx(s.control)}
        labelClassName={s.fieldGroupLabel}
        label="Description"
        {...other}
      >
        <TextField
          variant="outlined"
          fullWidth
          multiline
          id="description"
          disabled={disabled}
          placeholder="Describe the tag..."
          value={description}
          onChange={handleDescriptionChange}
          required
        />
        {errors.description && (
          <FieldError hideIcon={true} className={s.error}>
            {errors.description}
          </FieldError>
        )}
      </FieldsGroup>
      <Typography variant="h4" className={s.label}>
        Color
      </Typography>

      <TagColorPicker name={title} color={color} onChange={handleColorChange} />

      <LoadingButton
        loading={inFlight}
        className={s.button}
        fullWidth
        variant="contained"
        children={`${tag?.id ? "Save" : `Create ${tagType.toLocaleLowerCase()} tag`}`}
        onClick={handleUpsert}
        disabled={!title || inFlight || !!error}
      />
    </BaseDialog>
  );
}
